import { Check, Copy, ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "components/products/muggleTestV0/components/ui/button";
import { AGENT_INSTALL_COPIED_RESET_MS, AGENT_INSTALL_OPTIONS } from "constants/muggleTest/agentInstall";
import { cn } from "products/muggleTestV0/lib/utils";
import { AgentInstallActionEnum } from "types/muggleTest/agentInstall/AgentInstallActionEnum";
import { AgentInstallTargetEnum } from "types/muggleTest/agentInstall/AgentInstallTargetEnum";
import { IAgentInstallOption } from "types/muggleTest/agentInstall/IAgentInstallOption";

interface AgentInstallButtonsProps {
  className?: string;
}

/**
 * One button per coding agent that installs Muggle Works: opens the agent's install link where it
 * has one (Claude Desktop, Cursor), and copies the install text where it does not (Codex, Antigravity).
 * The next step for the clicked agent is shown under the row.
 *
 * @param props.className - Extra classes for the outer wrapper.
 */
export function AgentInstallButtons({ className }: AgentInstallButtonsProps) {
  const [selectedTarget, setSelectedTarget] = useState<AgentInstallTargetEnum | null>(null);
  const [copiedTarget, setCopiedTarget] = useState<AgentInstallTargetEnum | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);

  useEffect(() => {
    if (copiedTarget === null) return;
    const timer = setTimeout(() => setCopiedTarget(null), AGENT_INSTALL_COPIED_RESET_MS);
    return () => clearTimeout(timer);
  }, [copiedTarget]);

  const selectedOption = AGENT_INSTALL_OPTIONS.find((option) => option.target === selectedTarget);

  const onInstallClick = async (option: IAgentInstallOption) => {
    setSelectedTarget(option.target);
    setCopyFailed(false);
    if (option.action === AgentInstallActionEnum.OPEN_INSTALL_LINK) {
      window.location.href = option.installValue;
      return;
    }
    try {
      await navigator.clipboard.writeText(option.installValue);
      setCopiedTarget(option.target);
    } catch (error) {
      console.error("[AgentInstallButtons] Clipboard write failed", { target: option.target, error: error });
      setCopyFailed(true);
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex flex-wrap gap-2">
        {AGENT_INSTALL_OPTIONS.map((option) => {
          const isCopied = copiedTarget === option.target;
          const isLink = option.action === AgentInstallActionEnum.OPEN_INSTALL_LINK;
          return (
            <Button
              key={option.target}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void onInstallClick(option)}
              aria-label={isLink ? `Install in ${option.label}` : `Copy ${option.label} install`}
            >
              {isLink ? (
                <ExternalLink aria-hidden={true} />
              ) : isCopied ? (
                <Check aria-hidden={true} />
              ) : (
                <Copy aria-hidden={true} />
              )}
              {option.label}
            </Button>
          );
        })}
      </div>
      <p aria-live="polite" className="min-h-5 text-xs text-muted-foreground">
        {selectedOption &&
          (copyFailed ? `Couldn't copy. Install text: ${selectedOption.installValue}` : selectedOption.afterClickHint)}
      </p>
    </div>
  );
}
